import { alert } from '@/services/alert';
import { useMedplum } from '@medplum/react';
import { getReferenceString } from '@medplum/core';
import { Attachment, Binary } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import { getIvrAudioBinaryReference } from '@bariendo/lib/ivr';

/**
 * Uploads a picked file as a Binary and hands back the attachment pointing at it.
 *
 * A failed upload leaves the existing attachment alone rather than clearing it — losing a
 * working recording because the replacement failed to upload is the worse outcome.
 */
export function useIvrAudioUpload(onAttachmentChange: (attachment: Attachment | undefined) => void): {
  uploading: boolean;
  handleFileChange: (file: File | null) => void;
} {
  const medplum = useMedplum();
  const [uploading, setUploading] = useState(false);

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (!file) {
        onAttachmentChange(undefined);
        return;
      }

      async function upload(picked: File) {
        try {
          setUploading(true);
          const binary = await medplum.createBinary({ data: picked, contentType: picked.type });
          onAttachmentChange({
            url: getReferenceString(binary),
            contentType: picked.type,
            title: picked.name,
          });
        } catch (error) {
          alert.error(`Failed to upload audio file: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
          setUploading(false);
        }
      }

      void upload(file);
    },
    [medplum, onAttachmentChange]
  );

  return { uploading, handleFileChange };
}

/**
 * A playable URL for an attachment, resolved from the Binary rather than from the stored URL.
 *
 * A saved storage URL carries an Expires parameter, so preferring it is what made the preview
 * show 0:00 / 0:00 for a file that had gone stale — the same staleness that transfers real
 * callers (BAR-2492).
 */
export function useIvrAudioPlaybackUrl(audioFile: Attachment | undefined): string | undefined {
  const medplum = useMedplum();
  const [playbackUrl, setPlaybackUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!audioFile?.url) {
      setPlaybackUrl(undefined);
      return undefined;
    }

    const binaryReference = getIvrAudioBinaryReference(audioFile.url);

    if (!binaryReference) {
      setPlaybackUrl(audioFile.url);
      return undefined;
    }

    let cancelled = false;
    void medplum.readReference<Binary>({ reference: binaryReference }).then(
      (binary) => !cancelled && setPlaybackUrl(binary.url),
      () => !cancelled && setPlaybackUrl(undefined)
    );

    // Uploading a replacement while the previous read is in flight would otherwise let the
    // stale response win and leave the player pointing at the old file.
    return () => {
      cancelled = true;
    };
  }, [audioFile, medplum]);

  return playbackUrl;
}
